import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  getJournalEntries, postJournalEntry, voidJournalEntry, deleteJournalEntry, getFiscalYears,
} from "../../api/accountingApi";

const JournalEntryListPage = () => {
  const toast = useRef(null);
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ fiscalYearId: null, status: null, dateFrom: null, dateTo: null });
  const [voidDialog, setVoidDialog] = useState({ visible: false, entryId: null, reason: "" });

  useEffect(() => {
    loadFiscalYears();
  }, []);

  const loadFiscalYears = async () => {
    try {
      const data = await getFiscalYears();
      setFiscalYears(data);
      // Auto-select open fiscal year
      const openFy = data.find((fy) => fy.status === "open");
      if (openFy) {
        setFilters((prev) => ({ ...prev, fiscalYearId: openFy.id }));
        loadEntries({ ...filters, fiscalYearId: openFy.id });
      } else {
        setLoading(false);
      }
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load fiscal years" });
      setLoading(false);
    }
  };

  const loadEntries = async (f = filters) => {
    try {
      setLoading(true);
      const params = {};
      if (f.fiscalYearId) params.fiscalYearId = f.fiscalYearId;
      if (f.status) params.status = f.status;
      if (f.dateFrom) params.dateFrom = f.dateFrom.toISOString().split("T")[0];
      if (f.dateTo) params.dateTo = f.dateTo.toISOString().split("T")[0];

      const data = await getJournalEntries(params);
      setEntries(data);
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load journal entries" });
    } finally {
      setLoading(false);
    }
  };

  const handlePost = (entry) => {
    confirmDialog({
      message: `Post entry ${entry.entry_number}? Posted entries affect account balances and cannot be edited.`,
      header: "Post Journal Entry",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await postJournalEntry(entry.id);
          toast.current?.show({ severity: "success", summary: "Success", detail: "Entry posted" });
          loadEntries();
        } catch (err) {
          toast.current?.show({
            severity: "error", summary: "Error",
            detail: err.response?.data?.message || "Failed to post entry",
          });
        }
      },
    });
  };

  const handleVoid = async () => {
    if (!voidDialog.reason) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Void reason is required" });
      return;
    }

    try {
      await voidJournalEntry(voidDialog.entryId, voidDialog.reason);
      toast.current?.show({ severity: "success", summary: "Success", detail: "Entry voided" });
      setVoidDialog({ visible: false, entryId: null, reason: "" });
      loadEntries();
    } catch (err) {
      toast.current?.show({
        severity: "error", summary: "Error",
        detail: err.response?.data?.message || "Failed to void entry",
      });
    }
  };

  const handleDelete = (entry) => {
    confirmDialog({
      message: `Delete draft entry ${entry.entry_number}? This cannot be undone.`,
      header: "Delete Journal Entry",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteJournalEntry(entry.id);
          toast.current?.show({ severity: "success", summary: "Success", detail: "Entry deleted" });
          loadEntries();
        } catch (err) {
          toast.current?.show({
            severity: "error", summary: "Error",
            detail: err.response?.data?.message || "Failed to delete entry",
          });
        }
      },
    });
  };

  const statusTemplate = (rowData) => {
    const colors = { draft: "warning", posted: "success", void: "danger" };
    return <Tag value={rowData.status} severity={colors[rowData.status]} />;
  };

  const totalDebitTemplate = (rowData) => {
    const total = (rowData.lines || []).reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
    return total.toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const totalCreditTemplate = (rowData) => {
    const total = (rowData.lines || []).reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
    return total.toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const actionsTemplate = (rowData) => (
    <div className="flex gap-1">
      <Button
        icon="pi pi-eye"
        className="p-button-rounded p-button-text p-button-sm"
        onClick={() => navigate(`/index/accounting/journal-entries/${rowData.id}`)}
        tooltip="View / Edit"
      />
      {rowData.status === "draft" && (
        <>
          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text p-button-success p-button-sm"
            onClick={() => handlePost(rowData)}
            tooltip="Post"
          />
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-text p-button-danger p-button-sm"
            onClick={() => handleDelete(rowData)}
            tooltip="Delete"
          />
        </>
      )}
      {rowData.status === "posted" && (
        <Button
          icon="pi pi-ban"
          className="p-button-rounded p-button-text p-button-warning p-button-sm"
          onClick={() => setVoidDialog({ visible: true, entryId: rowData.id, reason: "" })}
          tooltip="Void"
        />
      )}
    </div>
  );

  const statusOptions = [
    { label: "All", value: null },
    { label: "Draft", value: "draft" },
    { label: "Posted", value: "posted" },
    { label: "Void", value: "void" },
  ];

  const leftToolbar = () => (
    <div className="flex flex-wrap gap-2 align-items-center">
      <Dropdown
        value={filters.fiscalYearId}
        options={fiscalYears.map((fy) => ({ label: fy.name, value: fy.id }))}
        onChange={(e) => setFilters({ ...filters, fiscalYearId: e.value })}
        placeholder="Fiscal Year"
        className="w-12rem"
      />
      <Dropdown
        value={filters.status}
        options={statusOptions}
        onChange={(e) => setFilters({ ...filters, status: e.value })}
        placeholder="Status"
        className="w-9rem"
      />
      <Calendar
        value={filters.dateFrom}
        onChange={(e) => setFilters({ ...filters, dateFrom: e.value })}
        placeholder="From Date"
        dateFormat="yy-mm-dd"
        showIcon
        className="w-10rem"
      />
      <Calendar
        value={filters.dateTo}
        onChange={(e) => setFilters({ ...filters, dateTo: e.value })}
        placeholder="To Date"
        dateFormat="yy-mm-dd"
        showIcon
        className="w-10rem"
      />
      <Button label="Load" icon="pi pi-search" className="p-button-sm" onClick={() => loadEntries()} />
    </div>
  );

  const rightToolbar = () => (
    <Button
      label="New Entry"
      icon="pi pi-plus"
      className="p-button-sm p-button-success"
      onClick={() => navigate("/index/accounting/journal-entries/new")}
    />
  );

  if (loading && entries.length === 0 && !filters.fiscalYearId) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="mb-3">
        <h2 className="m-0">Journal Entries</h2>
        <p className="text-500 mt-1 mb-0">View and manage double-entry journal entries</p>
      </div>

      <Toolbar className="mb-3" left={leftToolbar} right={rightToolbar} />

      <div className="card">
        <DataTable
          value={entries}
          loading={loading}
          paginator
          rows={20}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage="No journal entries found."
          className="p-datatable-sm"
          scrollable
          scrollHeight="calc(100vh - 380px)"
        >
          <Column field="entry_number" header="Entry #" sortable style={{ minWidth: "9rem" }} />
          <Column field="date" header="Date" sortable style={{ minWidth: "8rem" }} />
          <Column field="description" header="Description" style={{ minWidth: "14rem" }} />
          <Column field="reference" header="Reference" style={{ minWidth: "8rem" }} />
          <Column header="Total Debit" body={totalDebitTemplate} style={{ minWidth: "8rem", textAlign: "right" }} />
          <Column header="Total Credit" body={totalCreditTemplate} style={{ minWidth: "8rem", textAlign: "right" }} />
          <Column header="Status" body={statusTemplate} style={{ minWidth: "6rem" }} />
          <Column header="Actions" body={actionsTemplate} style={{ width: "10rem" }} />
        </DataTable>
      </div>

      {/* Void Dialog */}
      <Dialog
        header="Void Journal Entry"
        visible={voidDialog.visible}
        style={{ width: "400px" }}
        modal
        onHide={() => setVoidDialog({ visible: false, entryId: null, reason: "" })}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setVoidDialog({ visible: false, entryId: null, reason: "" })} />
            <Button label="Void Entry" icon="pi pi-ban" className="p-button-danger" onClick={handleVoid} />
          </div>
        }
      >
        <div className="flex flex-column gap-2 pt-2">
          <label htmlFor="void-reason">Reason for voiding *</label>
          <InputTextarea
            id="void-reason"
            value={voidDialog.reason}
            onChange={(e) => setVoidDialog({ ...voidDialog, reason: e.target.value })}
            rows={3}
            placeholder="Enter reason..."
          />
        </div>
      </Dialog>

      <ConfirmDialog />
      <Toast ref={toast} />
    </div>
  );
};

export default JournalEntryListPage;
